import { Suspense, useEffect, type CSSProperties } from "react";
import { Outlet } from "react-router-dom";
import {
  HEADER_HEIGHT_WITH_STRIP,
  useIsImpersonating,
} from "@/features/auth/impersonation/ImpersonationBanner";
import { AppSidebar } from "@/components/layout/sidebar/AppSidebar/AppSideBar";
import Header from "@/components/layout/header/Header";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { TooltipProvider } from "@/components/ui/tooltip";
import { PageHeaderProvider } from "@/contexts/PageHeaderContext";
import GmailComposeHost from "@/features/gmail/components/GmailComposeHost";
import GmailOAuthReturn from "@/features/gmail/components/GmailOAuthReturn";
import IncomingCallHost from "@/features/calls/live/IncomingCallHost";
import LeadsPrefetch from "@/features/leads/api/prefetchLeadsList";
import AIChatPanel from "@/features/ai/components/AIChatPanel";
import ShuffleSummaryModal from "@/features/notifications/components/ShuffleSummaryModal";
import ShuffleWarningModal from "@/features/notifications/components/ShuffleWarningModal";
import RecaptureAlertHost from "@/features/notifications/recapture/RecaptureAlertHost";
import AlertAnnounceHost from "@/features/alerts/announce/AlertAnnounceHost";
import SourceReconnectAlertHost from "@/features/sourceAndCampaign/components/reconnect/SourceReconnectAlertHost";
import ChatPopupAlertHost from "@/features/chat/alerts/ChatPopupAlertHost";
import FollowUpNotifier from "@/features/leads/components/FollowUpNotifier";
import InstallmentDueNotifier from "@/features/leads/components/InstallmentDueNotifier";
import CheckInGate from "@/features/checkIn/components/CheckInGate";
import { prefetchCommonPages } from "@/routes/registry";
import { OnboardingTourProvider } from "@/features/onboardingTour/OnboardingTourProvider";

export default function AppLayout() {
  // "Switch user": the app bar carries a "Viewing as …" strip, and every
  // page height in this column derives from --header-height — so the
  // column's variable grows by the strip. Set here, on the main column, not
  // on :root: the sidebar's own header keeps its 4rem.
  const impersonating = useIsImpersonating();
  const insetStyle = impersonating
    ? ({ "--header-height": HEADER_HEIGHT_WITH_STRIP } as CSSProperties)
    : undefined;
  useEffect(() => prefetchCommonPages(), []);
  // Default to the collapsed icon-rail; users can expand via the toggle.
  return (
    <SidebarProvider defaultOpen={false}>
      {/* Welcome tour + getting-started checklist for new workspaces (and
          "Take the tour" in the profile menu). Inside the sidebar provider:
          the tour folds the sidebar to its icon rail while it talks. */}
      <OnboardingTourProvider>
      <TooltipProvider>
        <AppSidebar />
        <SidebarInset style={insetStyle}>
          <PageHeaderProvider>
            <Header />
            {/* Pages load on demand (routes/registry.tsx): only the page area
                waits for its chunk — the sidebar and header stay up. */}
            <Suspense
              fallback={
                <div className="flex flex-1 items-center justify-center">
                  <div className="loader" />
                </div>
              }
            >
              <Outlet />
            </Suspense>
          </PageHeaderProvider>
        </SidebarInset>
        {/*
          Gmail compose modal at app-root so Quick Send can fire from any
          screen (lead drawer, dynamic stage, dashboard, etc.) without
          requiring the user to be on /gmail.
        */}
        <GmailComposeHost />
        {/* Google consent popups (Gmail connect / inbox access) report back here. */}
        <GmailOAuthReturn />
        {/*
          Incoming-call cards (dynamic call routing) — top-right, like a call
          from your iPhone on a Mac. Inside the Router: "Open lead" navigates.
        */}
        <IncomingCallHost />
        {/*
          Background prefetch of the lead-list trio (data + count + stats)
          ~600ms after auth clears. By the time the user clicks "Leads" in
          the sidebar, the React Query cache is already hot and the page
          renders synchronously off the cache. Renders nothing.
        */}
        <LeadsPrefetch />
        {/*
          AI assistant — right-side floating chat panel. Mounted once at
          app root so it's reachable from any screen (Cursor pattern).
          Renders nothing when Tenant.isAIEnabled is false. Toggle with
          ⌘K or the header AIToggleButton.
        */}
        <AIChatPanel />
        {/*
          Shuffle accountability — login-time modal listing every automation
          shuffle that took/gave the user leads since they last acknowledged
          (unread `automation_shuffle` notifications). Mounted in the layout
          (inside the router) so its "View lost leads" deep link can navigate.
          Closing marks them read (DB-backed), so it shows exactly once.
        */}
        <ShuffleSummaryModal />
        {/*
          Pre-shuffle warning — pops LIVE (~one sweep tick ahead) when the
          user's leads are about to be reshuffled, so acting on them keeps
          them. Driven by unread `automation_shuffle_warning` notifications;
          the socket invalidation refetches it in real time.
        */}
        <ShuffleWarningModal />
        {/*
          Recapture popup — pops when a lead assigned to this user re-enquires
          through a source (server pushes `lead:recaptured` to assignees only).
          Mounted in the layout (inside the router) so "View lead" can navigate.
        */}
        <RecaptureAlertHost />
        {/*
          Alerts (admin/manager → the roles under them). An ANNOUNCE alert
          pops here — live on `alert:new`, and on login while still unread
          (the inbox's `pendingAnnouncements`). "Got it" marks it read.
        */}
        <AlertAnnounceHost />
        {/*
          "Reconnect required" — Super Admins only. A lead source (Meta) stopped
          syncing in a way only a person can fix; shows on login while the
          server flag is open and live on `source:reconnect_required`.
          "Reconnect now" deep-links to the source's edit drawer.
        */}
        <SourceReconnectAlertHost />
        {/* @-mentions (always) and chat attachments (per Settings → Manage →
            "Pop assigned users") interrupt with a modal instead of a toast. */}
        <ChatPopupAlertHost />
        {/*
          Follow-up due popup — pops the minute a follow-up this user OWNS comes
          due (server pushes `followup:due` to personal-scope assignees only).
          Lives here rather than AppProviders so "View Lead" can navigate.
        */}
        <FollowUpNotifier />
        <InstallmentDueNotifier />
        {/*
          "Check-in / Check-out" — the "Please check in first" prompt for
          personal-scope users, plus their heartbeat. Renders nothing for
          everyone the feature doesn't apply to.
        */}
        <CheckInGate />
      </TooltipProvider>
      </OnboardingTourProvider>
    </SidebarProvider>
  );
}
